import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PrintPopup.css';
import { BsX } from "react-icons/bs";
import { useAtomValue, useSetAtom } from 'jotai';
import { fiwareCatalogDataItemsAtom, isPrinterControlOpenAtom, mainMapAtom, mapClickEventAtom, menuItemsAtom, printAttributionsAtom, selectedBaseMapAtom, selectedFeatureAtom, selectedLayersAtom } from '../../atoms';
import SquareButton from '../button/SquareButton';
import { MEASURE } from '../../constants/components/printpopup';
import { MAIN_MAP_ID } from '../../constants/components/map';
import Legend from '../legend/Legend';
import { anyType } from '../../types';
import { useGetTileUrlsByType } from '../../lib/useGetTileUrls';
import AttributeTable from '../attributeTable/AttributeTable';
import { QRCodeSVG } from 'qrcode.react';
import { ZoomConfig } from '../../lib/zoomValidation';
import { MapGeoJSONFeature } from 'maplibre-gl';
import { FormatText } from '../../lib/FormatText';
import classNames from 'classnames';
import { updateFilteredFeatures } from '../../lib/updateFilteredFeatures';
import { addPrintClickPointLayer } from '../../lib/addPrintClickPointLayer';

declare global {
  interface Window {
    printMap?: maplibregl.Map;
  }
}


const PrintPopup: React.FC = () => {

    const mapContainer = useRef<HTMLDivElement>(null);
    const qrCodeRef = useRef<SVGSVGElement>(null);
    const printTextAreaRef = useRef<HTMLDivElement>(null);
    const mapAreaRef = useRef<HTMLDivElement>(null);
    const [printMap, setPrintMap] = useState<maplibregl.Map | undefined>(undefined);
    const dataPngUrls = useGetTileUrlsByType('datapng');
    const vectorTileUrls = useGetTileUrlsByType('vector');

    // atoms
    const setIsPrinterControlOpen = useSetAtom(isPrinterControlOpenAtom);
    const selectedLayers = useAtomValue(selectedLayersAtom);
    const mainMap = useAtomValue(mainMapAtom);
    const selectedFeature = useAtomValue(selectedFeatureAtom);
    const fiawreCatalogDataItems = useAtomValue(fiwareCatalogDataItemsAtom);
    const menuItem = useAtomValue(menuItemsAtom);
    const clickEvent = useAtomValue(mapClickEventAtom);
    const selectedBaseMap = useAtomValue(selectedBaseMapAtom);

    const printAttributions = useAtomValue(printAttributionsAtom);

    const [text, setText] = useState<string | undefined>(undefined);
    const [showText, setShowText] = useState<boolean>(true);
    const [showLegend, setShowLegend] = useState<boolean>(false);
    const [showQRCode, setShowQRCode] = useState<boolean>(true);
    const [showAttribute, setShowAttribute] = useState<boolean>(false);
    const [filterdFeatureIds, setFilterdFeatureIds] = useState<string[]>([]);

    const [zoomErrMsg, setZoomErrMsg] = useState<string | undefined>(undefined);
    const [nowZoom, setNowZoom] = useState<number | undefined>(undefined);

    const [hasZoomConfIdsObj, setHasZoomConfIdsObj] = useState<{ [key: string]: ZoomConfig } | undefined>(undefined);
    const mainMapZoom = useMemo(() => mainMap?.getZoom() || mainMap?.getMinZoom() || 14, [mainMap]);

    const [measureObj, setMeasureObj] = useState<{ label: string; value: number, isShow: boolean }[]>([]);


    const zoomLabel = useMemo(() => {
        return MEASURE(0).find((item) => item.value === nowZoom)?.label ?? '任意の縮尺';
    }, [nowZoom]);



    /* ************************
     * 高さを調整し、ページを分割
     * ************************/
    const adjustPageBreaks = () => {
        const sections = document.querySelectorAll(".print-section");
        let currentPageHeight = 0;
        const maxPageHeight = 800; // 印刷サイズに応じた高さを取得

        sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            currentPageHeight += rect.height;

            if (currentPageHeight > maxPageHeight) {
                section.classList.add("page-break");
                currentPageHeight = rect.height; // 次のページの高さリセット
            }
        });
    };


    /* ************************
     * チェックボックス変更時処理
     * ************************/
    const onChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const value = e.target.value;

        switch (value) {
            case 'displayTemplate':
                setShowText(checked);
                break;
            case 'displayLegend':
                setShowLegend(checked);
                break;
            case 'displayAttributeInformation':
                setShowAttribute(checked);
                break;
            case 'displayQRCode':
                setShowQRCode(checked);
                break;
            default:
                break;
        }
    };

    const handlePrint = () => {
        adjustPageBreaks();
        window.print();
    };

    /* ************************
     * 印刷の定型文を取得
     * ************************/
    useEffect(() => {
        const fetchData = async () => {
            try {
                const disclaimer = await fetch('https://raw.githubusercontent.com/yaizu-city/app/main/印刷/注意書き.csv');
                const resText = await disclaimer.text();
                setText(resText);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);


    /* ************************
     * 縮尺の設定
     * ************************/
    const handleZoomChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!printMap) { return; }
        const zoom = parseFloat(event.target.value);

        if (selectedFeature) {
            const center = getFeatureCenter(selectedFeature);
            if (center) {
                printMap.setCenter(center);
            }
        }

        printMap.setZoom(zoom);
        setNowZoom(zoom);
    };

    const getFeatureCenter = (features: { [key: string]: MapGeoJSONFeature[] }): [number, number] | null => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        Object.values(features).forEach(featureArray => {
            featureArray.forEach(feature => {
                if (feature.geometry && 'coordinates' in feature.geometry) {
                    const coordinates = feature.geometry.coordinates;
                    if (feature.geometry.type === 'Point') {
                        const [x, y] = coordinates as [number, number];
                        minX = Math.min(minX, x);
                        minY = Math.min(minY, y);
                        maxX = Math.max(maxX, x);
                        maxY = Math.max(maxY, y);
                    } else if (feature.geometry.type === 'Polygon') {
                        (coordinates as number[][]).forEach(([x, y]) => {
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x);
                            maxY = Math.max(maxY, y);
                        });
                    }
                }
            });
        });

        if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
            return null;
        }

        return [(minX + maxX) / 2, (minY + maxY) / 2];
    };

    /* ************************
     * 印刷用マップの設定
     * ************************/
    useEffect(() => {
        if (!mainMap || printMap) { return; }
        const zoom = mainMap.getZoom();
        const map = new window.geolonia.Map({
            container: mapContainer.current!,
            style: mainMap.getStyle(),
            center: mainMap.getCenter(),
            pitch: mainMap.getPitch(),
            zoom: zoom,
            minZoom: mainMap.getMinZoom(),
            maxZoom: mainMap.getMaxZoom(),
            localIdeographFontFamily: 'sans-serif'
        }) as unknown as maplibregl.Map;

        const baseMapZoom = selectedBaseMap?.printzoom ? selectedBaseMap.printzoom.split(',') : [];

        map.scrollZoom.disable();
        map.doubleClickZoom.disable();
        map.boxZoom.disable();
        map.touchZoomRotate.disable();
        map.dragPan.disable();
        map.keyboard.disable();

        const loadedMap = async () => {

            if (clickEvent) {
                addPrintClickPointLayer(map, clickEvent.lngLat.lng, clickEvent.lngLat.lat);
            }

            map.on('styleimagemissing', (e) => {
                const spriteId = e.id;
                const url = `${location.protocol}//${location.host}${location.pathname}sprites/${spriteId}.png`;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                map.loadImage(url, (error: anyType, image: anyType) => {
                    if (error) { console.error(error); }
                    if (image && !map.hasImage(spriteId)) {
                        map.addImage(spriteId, image);
                    }
                });
            });

            setPrintMap(map);

            // Playwright のテストのために window.printMap に mapオブジェクトをセット
            window.printMap = map;

            const result = await updateFilteredFeatures({
                map,
                menuItem,
                dataPngUrls,
                vectorTileUrls,
                hasZoomConfIdsObj,
                measureObj: MEASURE(mainMapZoom),
                baseMapZoom
            });
            setFilterdFeatureIds(result.filterdFeatureIds);
            setZoomErrMsg(result.zoomErrMsg);
            if (result.measureObj !== undefined) {
                setMeasureObj(result.measureObj);
            }
            if (result.hasZoomConfIdsObjCurrent !== undefined) { setHasZoomConfIdsObj(result.hasZoomConfIdsObjCurrent); }
        };

        map.once('load', loadedMap);

        return () => {
            map.off('load', loadedMap);
        };

    }, [mapContainer, mainMap, fiawreCatalogDataItems, mainMapZoom]);


    useEffect(() => {
        if (!printMap || !measureObj || measureObj.length === 0) { return; }
        if (printMap.getZoom() === measureObj[0].value) { return; }
        (printMap as maplibregl.Map).setZoom(measureObj[0].value);
        setNowZoom(measureObj[0].value);
    }, [printMap, measureObj]);


    /* ************************
     * 印刷用マップの設定
     * ************************/
    useEffect(() => {
        if (!selectedLayers || !(MAIN_MAP_ID in selectedLayers)) { return; }
        // Note: 行政区域界を常に選択している為、選択レイヤーが1つの場合は凡例を表示しない
        setShowLegend(selectedLayers.length > 1);
    }, [selectedLayers]);


    return (
        <div className='print-popup-overlay' onClick={() => setIsPrinterControlOpen(false)}>
            <div className='print-popup' onClick={(event) => event.stopPropagation()}>
                <div className='print-popup-header'>
                    <h2>地図を印刷</h2>
                    <BsX className='icon' onClick={() => setIsPrinterControlOpen(false)} />
                </div>
                <div className='print-popup-content'>
                    <div className='print-container'>
                        <div className='map-area' ref={mapAreaRef}>
                            <div className='zoomlevel-panel'>
                                {`現在の縮尺：${zoomLabel}`}
                            </div>
                            <div
                                className='map'
                                ref={mapContainer}
                                data-lang="ja"
                                data-gesture-handling="off"
                                data-navigation-control="off"
                                data-scale-control="on"
                            ></div>
                        </div>
                        <div className='content-area'>
                            <div className='description-area'>
                                {
                                    showLegend &&
                                    <div className='print-legend-area'>
                                        <h4>凡例</h4>
                                        <div className='legend-wrapper'>
                                            {filterdFeatureIds.length > 0 ?
                                                <Legend small filterLayers={filterdFeatureIds} />
                                                :
                                                <p className='description'>凡例を表示するには、データを印刷画面の地図上に表示する必要があります。</p>
                                            }
                                        </div>
                                    </div>
                                }
                                {
                                    (selectedFeature && showAttribute) &&
                                    <div className='print-attribute-area'>
                                        <h4>属性情報</h4>
                                        <AttributeTable attributes={selectedFeature} />
                                    </div>
                                }
                            </div>
                            <div className='print-text-area' ref={printTextAreaRef}>
                                {/* 定型文 */}
                                {showText &&
                                    <div className={classNames('template-text', { 'qr-code-adjacent': showQRCode })}>
                                        <p><FormatText text={text ?? ''} /></p>
                                        {printAttributions &&
                                            <div className='attribution-area'>
                                                <p className='bold'>【出展】</p>
                                                <div className='attribution-list'>{
                                                    Object.values(printAttributions).map((item, index) => {
                                                        return <div key={index}>
                                                            <p >{item['出展者']} : </p>
                                                            <p>
                                                                <span><FormatText text={item['url']} /></span>
                                                                {item['タイル名'] && <span>（{item['タイル名']}）</span>}
                                                            </p>
                                                        </div>
                                                    })
                                                }</div>
                                            </div>
                                        }
                                    </div>
                                }
                                {/* QRコード */}
                                {showQRCode &&
                                    <div className='qr-code-area'>
                                        <QRCodeSVG
                                            value={'https://www.city.yaizu.lg.jp/life/digital-cityoffice/smart-maps.html#rules'}
                                            size={64}
                                            bgColor={"#ffffff"}
                                            fgColor={"#555"}
                                            ref={qrCodeRef}
                                        />
                                        <p className='small-text'>詳細の注意事項はこちら</p>
                                    </div>
                                }
                            </div>
                            {/* コピーライト */}
                            <p className='copyright'>Copyright © {new Date().toLocaleDateString().split('/')[0]} Yaizucity. All rights reserved.</p>
                        </div>
                    </div>
                    <div className='print-config-container'>
                        <h3>印刷設定</h3>
                        <div className='config-item'>
                            {(hasZoomConfIdsObj && zoomErrMsg) ?
                                <>
                                    <p className='small-text'>以下のレイヤーは、指定の縮尺で印刷する必要があります。</p>
                                    <p className='error-text small-text'>{zoomErrMsg}</p>
                                    <ul className='zoom-list'>
                                        {
                                            Object.keys(hasZoomConfIdsObj).map((key) => {
                                                const obj = hasZoomConfIdsObj;
                                                if (!obj) { return null; }

                                                return <li key={key}>{key} :
                                                    <>
                                                        <span>{obj[key].minzoom && `zoom${obj[key].minzoom}以上`}</span>
                                                        <span>{obj[key].maxzoom && `zoom${obj[key].maxzoom}以下`}</span>
                                                    </>
                                                </li>
                                            })
                                        }
                                    </ul>
                                </>
                                :
                                measureObj.filter((item) => item.isShow).length > 0 &&
                                <>
                                    <h4>尺度</h4>
                                    <label className="selectbox">
                                        <select onChange={handleZoomChange}>
                                            {
                                                measureObj.filter((item) => item.isShow).map((item) => {
                                                    return <option key={item.label} value={item.value}>{item.label}</option>
                                                })
                                            }
                                        </select>
                                    </label>
                                    <p className='small-text'>※ 縮尺を変更すると、属性情報を選択したいる場合、一部または全ての属性情報が表示されなくなる可能性があります。</p>
                                </>
                            }
                        </div>

                        <div className='config-item'>
                            <h4>表示設定</h4>
                            <div className='checkBox-container'>
                                <label className="checkbox">
                                    <input
                                        type="checkbox"
                                        value='displayLegend'
                                        disabled={selectedLayers && selectedLayers.length === 0}
                                        checked={showLegend}
                                        onChange={onChangeCheckbox}
                                    />
                                    <span>凡例を表示</span>
                                </label>
                                <label className="checkbox">
                                    <input
                                        type="checkbox"
                                        value='displayAttributeInformation'
                                        checked={showAttribute}
                                        onChange={onChangeCheckbox}
                                        disabled={!selectedFeature}
                                    />
                                    <span>属性情報を表示</span>
                                </label>
                                <p className='small-text'>※ 印刷画面を開く前に選択した属性情報を印刷します</p>
                                <label className="checkbox">
                                    <input
                                        type="checkbox"
                                        value='displayTemplate'
                                        checked={showText}
                                        onChange={onChangeCheckbox}
                                    />
                                    <span>定型文を表示</span>
                                </label>
                                <label className="checkbox">
                                    <input
                                        type="checkbox"
                                        value='displayQRCode'
                                        checked={showQRCode}
                                        onChange={onChangeCheckbox}
                                    />
                                    <span>QRコードを表示</span>
                                </label>
                            </div>
                        </div>
                        <div className='bottom-btn-area'>
                            <SquareButton label='印刷する' onClick={handlePrint} disabled={zoomErrMsg !== undefined} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintPopup;
